import { AlertColor } from '@mui/material/Alert'
import {
  createAsyncThunk,
  createSlice,
  isAnyOf,
  PayloadAction,
} from '@reduxjs/toolkit'
import okaoAxios from '../helpers/axios'
import { convertToPredictedAndPublicRestaurantList } from '../helpers/converters'
import {
  ILandingCuisinesResponse,
  ILandingDetailsRequest,
  ILandingResponse,
  ILandingRestaurantList,
  IRestaurantsCityRequest,
  IRestaurantsCuisineRequest,
  LandingType,
  Restaurant,
  RestaurantAndPredictedRestaurant,
  RestaurantAndPredictedRestaurantResponse,
  // eslint-disable-next-line
  RestaurantResponse,
} from '../types'
import { RootState, store } from './'

export interface IRestaurantSlice {
  status: string
  shouldOpen: boolean
  message: string | undefined
  severity: AlertColor | undefined
  landingDetail: ILandingResponse | undefined
  localList: ILandingRestaurantList | undefined
  walletList: ILandingRestaurantList | undefined
  topRatedList: ILandingRestaurantList | undefined
  mustTryList: ILandingRestaurantList | undefined
  cuisineList: ILandingCuisinesResponse[] | []
  restaurants: RestaurantAndPredictedRestaurant[]
  pageSize: number
  page: number
  currentPage: number
  totalPages: number
  hasMore: boolean
}

const initialState: IRestaurantSlice = {
  status: 'idle',
  shouldOpen: false,
  message: undefined,
  severity: undefined,
  landingDetail: undefined,
  localList: undefined,
  walletList: undefined,
  topRatedList: undefined,
  mustTryList: undefined,
  cuisineList: [],
  restaurants: [],
  pageSize: 12,
  page: 0,
  currentPage: 0,
  totalPages: 1,
  hasMore: false,
}

export const getLandingPageDetails = createAsyncThunk(
  'api/getLandingPageDetails',
  async (payload: ILandingDetailsRequest) => {
    const response = await okaoAxios.post(`/landing`, payload)
    return response?.data
  },
)
export const getRestaurants = createAsyncThunk(
  'api/getRestaurants',
  async (payload: ILandingDetailsRequest) => {
    const { page, type, ...data } = payload
    const response = await okaoAxios.post(
      `/landing/${type as string}/all/${
        page?.toLocaleString() as string
      }?pageSize=${store.getState().landing.pageSize}`,
      data,
    )
    return response?.data
  },
)

export const getLandingPageCuisineDetails = createAsyncThunk(
  'api/getLandingPageCuisineDetails',
  async (payload: IRestaurantsCuisineRequest) => {
    const { name, page, ...apiPayload } = payload
    const response = await okaoAxios.post(
      `/landing/${name}/cuisine/${page ?? 0}?pageSize=${
        store.getState().landing.pageSize ?? 25
      }`,
      apiPayload,
    )
    return response?.data
  },
)
export const getLandingPageCityRestaurantDetails = createAsyncThunk(
  'api/getLandingPageCityRestaurantDetails',
  async (payload: IRestaurantsCityRequest) => {
    const { name, page, ...apiPayload } = payload
    // eslint-disable-next-line
    const response = await okaoAxios.post(
      `/landing/${name}/city/${page ?? 0}?pageSize=${
        store.getState().landing.pageSize ?? 25
      }`,
      apiPayload,
    )
    return response?.data
  },
)

export const getLandingListWithPredictions = createAsyncThunk(
  'api/getLandingListWithPredictions',
  async (payload: {
    type: LandingType
    page: number
    pageSize: number
    request: ILandingDetailsRequest
  }) => {
    const { type, page, pageSize, request } = payload
    const { longitude, latitude, radius } = request
    const response = await okaoAxios.post(
      `/landing/${type}/all/${page}?pageSize=${pageSize}`,
      {
        longitude,
        latitude,
        radius,
      },
    )
    return {
      type,
      data: response?.data,
    }
  },
)

export const landingSlice = createSlice({
  name: 'landing',
  initialState,
  reducers: {
    setOpen: (state, action: PayloadAction<boolean>) => {
      state.shouldOpen = action.payload
    },
    setRestaurants: (
      state,
      action: PayloadAction<{
        response: RestaurantAndPredictedRestaurantResponse
        isAuthenticated: boolean
      }>,
    ) => {
      // Normalize predictedRestaurants if they exist
      const normalizeRestaurantAndPredicted = (
        response: RestaurantAndPredictedRestaurant,
      ): RestaurantAndPredictedRestaurant => {
        // Only normalize if restaurantDTO exists
        if (!response.restaurantDTO) {
          return response
        }
        const dtoPayload = response.restaurantDTO as Restaurant & {
          imageurl?: string
        }
        const normalizedDTO: Restaurant = {
          ...dtoPayload,
          imageUrl: dtoPayload.imageUrl ?? dtoPayload.imageurl,
        }
        const normalized: RestaurantAndPredictedRestaurant = {
          label: response.label,
          value: response.value,
          groupValue: response.groupValue,
          starCount: response.starCount,
          recommendation: response.recommendation,
          groupRecommendations: response.groupRecommendations,
          restaurantDTO: normalizedDTO,
          group: response.group,
        }
        return normalized
      }

      const normalizedResponse = {
        ...action.payload.response,
        predictedRestaurants: action.payload.response?.predictedRestaurants?.map(
          normalizeRestaurantAndPredicted,
        ),
      }

      if (!action.payload.isAuthenticated) {
        if (normalizedResponse.page === 0) {
          state.restaurants = convertToPredictedAndPublicRestaurantList(
            normalizedResponse.result ?? [],
          )
        } else {
          if (state.page < normalizedResponse.page) {
            state.restaurants = [
              ...state.restaurants,
              ...convertToPredictedAndPublicRestaurantList(
                normalizedResponse.result ?? [],
              ),
            ]
          }
        }
      } else {
        if (normalizedResponse.page === 0) {
          state.restaurants =
            normalizedResponse?.predictedRestaurants &&
            normalizedResponse?.predictedRestaurants?.length
              ? normalizedResponse.predictedRestaurants
              : convertToPredictedAndPublicRestaurantList(
                  normalizedResponse.result ?? [],
                )
        } else {
          if (state.page < normalizedResponse.page) {
            state.restaurants = [
              ...state.restaurants,
              ...(normalizedResponse?.predictedRestaurants &&
              normalizedResponse?.predictedRestaurants?.length
                ? normalizedResponse.predictedRestaurants
                : convertToPredictedAndPublicRestaurantList(
                    normalizedResponse.result ?? [],
                  )),
            ]
          }
        }
      }

      state.totalPages = Math.ceil(
        normalizedResponse.totalCount / state.pageSize,
      )
      state.page = normalizedResponse.page
      state.status = 'idle'
    },
    clearData: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getLandingPageDetails.fulfilled, (state, action) => {
        // Normalize imageUrl in the landing response
        const normalizeRestaurant = (restaurant: Restaurant): Restaurant => {
          const restaurantWithLegacy = restaurant as Restaurant & {
            imageurl?: string
          }
          return {
            ...restaurant,
            imageUrl: restaurant.imageUrl ?? restaurantWithLegacy.imageurl,
          }
        }

        // Note: predictedRestaurants are now Prediction[] (not RestaurantResponse[])
        // They don't need normalization as they don't contain restaurantDTO

        const normalizedPayload: ILandingResponse = {
          ...action.payload,
          lists: action.payload?.lists?.map((list) => ({
            ...list,
            restaurants: list.restaurants?.map(normalizeRestaurant) ?? [],
            // predictedRestaurants are Prediction[] and don't need normalization
            predictedRestaurants: list.predictedRestaurants,
          })) ?? [],
        }

        state.landingDetail = normalizedPayload

        state.localList = normalizedPayload?.lists.find(
          (item) => item.type === LandingType.LOCAL,
        )
        state.walletList = normalizedPayload?.lists.find(
          (item) => item.type === LandingType.WALLET,
        )
        state.topRatedList = normalizedPayload?.lists.find(
          (item) => item.type === LandingType.TOPRATED,
        )
        state.mustTryList = normalizedPayload?.lists.find(
          (item) => item.type === LandingType.MUSTTRY,
        )

        state.cuisineList = normalizedPayload?.cuisines || []

        state.status = 'idle'
      })
      .addCase(getLandingListWithPredictions.fulfilled, (state, action) => {
        const { type, data } = action.payload

        // Normalize restaurants in the response
        const normalizeRestaurant = (restaurant: Restaurant): Restaurant => {
          const restaurantWithLegacy = restaurant as Restaurant & {
            imageurl?: string
          }
          return {
            ...restaurant,
            imageUrl: restaurant.imageUrl ?? restaurantWithLegacy.imageurl,
          }
        }

        // Normalize predictedRestaurants (Prediction[]) - they don't need restaurantDTO normalization
        const normalizedRestaurants =
          data?.result?.map(normalizeRestaurant) ?? []
        const normalizedPredictions = data?.predictedRestaurants ?? []

        // Get existing list to preserve name
        const existingList =
          type === LandingType.MUSTTRY
            ? state.mustTryList
            : type === LandingType.TOPRATED
              ? state.topRatedList
              : type === LandingType.WALLET
                ? state.walletList
                : state.localList

        // Create enriched ILandingRestaurantList, preserving existing name
        const enrichedList: ILandingRestaurantList = {
          name:
            existingList?.name ??
            (type === LandingType.MUSTTRY
              ? 'Must Try Restaurants'
              : type === LandingType.TOPRATED
                ? 'Top Rated Restaurants'
                : type === LandingType.WALLET
                  ? 'Wallet Friendly Restaurants'
                  : 'Local Flavors To Explore'),
          type,
          restaurants: normalizedRestaurants,
          predictedRestaurants: normalizedPredictions,
        }

        // Update the appropriate list in state
        switch (type) {
          case LandingType.MUSTTRY:
            state.mustTryList = enrichedList
            break
          case LandingType.TOPRATED:
            state.topRatedList = enrichedList
            break
          case LandingType.WALLET:
            state.walletList = enrichedList
            break
          case LandingType.LOCAL:
            state.localList = enrichedList
            break
        }

        // Don't change status - enrichment is silent
      })
      .addMatcher(
        isAnyOf(
          getLandingPageDetails.rejected,
          getRestaurants.rejected,
          getLandingPageCuisineDetails.rejected,
          getLandingPageCityRestaurantDetails.rejected,
          getLandingListWithPredictions.rejected
        ),
        (state, action) => {
          state.shouldOpen = true
          state.message =
            'Oops! Looks like something went wrong on our end. But don’t worry, a quick refresh might just do the trick! Give it a whirl and let’s try this again. If the problem persists, we’ll be here to sort it out!'
          state.status = 'error'
          state.severity = 'error'
          state.landingDetail = undefined
        },
      )
      .addMatcher(
        isAnyOf(
          getLandingPageDetails.pending,
          getRestaurants.pending,
          getLandingPageCuisineDetails.pending,
          getLandingPageCityRestaurantDetails.pending
        ),
        (state) => {
          state.status = 'loading'
        },
      )
  },
})

export const selectIsLoading = (state: RootState): boolean =>
  state.landing.status === 'loading'

export const selectShouldOpen = (state: RootState): boolean =>
  state.landing.shouldOpen
export const selectMessage = (state: RootState): string | undefined =>
  state.landing.message
export const selectSeverity = (state: RootState): AlertColor | undefined =>
  state.landing.severity
export const selectLandingDetail = (
  state: RootState,
): ILandingResponse | undefined => state.landing.landingDetail

export const selectLocalList = (
  state: RootState,
): ILandingRestaurantList | undefined => state.landing.localList
export const selectWalletList = (
  state: RootState,
): ILandingRestaurantList | undefined => state.landing.walletList
export const selectTopRatedList = (
  state: RootState,
): ILandingRestaurantList | undefined => state.landing.topRatedList
export const selectMustTryList = (
  state: RootState,
): ILandingRestaurantList | undefined => state.landing.mustTryList
export const selectCuisineList = (
  state: RootState,
): ILandingCuisinesResponse[] | [] => state.landing.cuisineList
export const selectRestaurants = (
  state: RootState,
): RestaurantAndPredictedRestaurant[] | undefined => state.landing.restaurants

export const selectTotalPages = (state: RootState): number =>
  state.landing.totalPages
export const selectPage = (state: RootState): number => state.landing.page
export const selectPageSize = (state: RootState): number =>
  state.landing.pageSize

export const { setOpen, setRestaurants, clearData } = landingSlice.actions

export default landingSlice.reducer
